import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import CSVReader from "react-csv-reader";

import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import ContactsIcon from '@material-ui/icons/Contacts';
import Box from '@material-ui/core/Box';
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import CloudDoneOutlinedIcon from '@material-ui/icons/CloudDoneOutlined';

import { splitRows, buildErrorsCsv } from '../../utils/csv-contacts';

import { setGroupFormField, createGroup, clearGroupForm } from '../../redux/actions/groups';
import { notistackError } from '../../redux/actions/notistack';
import { MAX_USERS_PER_GROUP } from '../../configs';


const CreateGroup = () => {
	const dispatch = useDispatch();
	const { name, members } = useSelector(state => state.groups.group_form);
	const { create_group_pending, create_group_success, form_errors } = useSelector(state => state.groups);
	const [formValid, setFormValid] = useState(false);
	const [csvUploadPending, setCsvUploadPending] = useState(false);
	// Does not seem like there is an easy way to reset file input field, use this to rerender input field
	const [inputFieldKey, setInputFieldKey] = useState('');
	// When a CSV has some invalid rows, hold the {valid, invalid} split here
	// and surface a dialog so the user can review, download the errors, then
	// either proceed with the valid subset or cancel and fix the file.
	const [errorReport, setErrorReport] = useState(null);

	useEffect(() => {
		const valid = (form_errors['name'] === false) && (form_errors['members'] === false);
		setFormValid(valid);
	}, [form_errors]);

	useEffect(() => {
		if (create_group_success) {
			dispatch(clearGroupForm());
		}
	}, [create_group_success]);

	useEffect(() => {
		// This ugly hack is needed because CSVReader does not have any callback for when file is uploaded
		const csvInput = window.document.getElementById('react-csv-reader-input')
		const eventListener = csvInput.addEventListener('change', (event) => {
			if (event.target.files.length > 0) {
				setCsvUploadPending(true);
			}
		})
		return () => {
			csvInput.removeEventListener('change', eventListener);
		}
	}, [dispatch]);

	const handleNameChange = (event) => {
		const value = event.target.value;
		dispatch(setGroupFormField('name', value));
	}

	const resetFileInput = () => {
		setInputFieldKey(new Date());
		dispatch(setGroupFormField('members', []));
	};

	const handleCsvFileLoaded = (data, fileInfo) => {
		if (data.length === 0) {
			resetFileInput();
			dispatch(notistackError("CSV file has no contacts. Please upload a file with contacts."));
			setCsvUploadPending(false);
			return;
		}

		if (data.length > MAX_USERS_PER_GROUP) {
			resetFileInput();
			dispatch(notistackError("CSV file has too many contacts. Consider splitting the file into smaller files."));
			setCsvUploadPending(false);
			return;
		}

		const { valid, invalid } = splitRows(data);

		if (invalid.length === 0) {
			dispatch(setGroupFormField('members', valid));
			setCsvUploadPending(false);
			return;
		}

		if (valid.length === 0) {
			resetFileInput();
			dispatch(notistackError(`All ${invalid.length} rows in the CSV are invalid. Please check the file and try again.`));
			setCsvUploadPending(false);
			return;
		}

		// Mixed result — let the user review and decide.
		setErrorReport({ valid, invalid });
		setCsvUploadPending(false);
	};

	const handleProceedWithValid = () => {
		dispatch(setGroupFormField('members', errorReport.valid));
		setErrorReport(null);
	};

	const handleCancelErrors = () => {
		resetFileInput();
		setErrorReport(null);
	};

	const handleDownloadErrors = () => {
		const csv = buildErrorsCsv(errorReport.invalid);
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${name || 'group'}_csv_errors.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	const handleSubmit = () => {
		dispatch(createGroup({ group_name: name, members }));
	}

	const renderIcon = () => {
		if (csvUploadPending) {
			return <CircularProgress disableShrink size={20} color="primary"/>;
		}
		if (members.length > 0) {
			return <CloudDoneOutlinedIcon size={21} color="primary"/>;
		}
		return <CloudUploadOutlinedIcon size={21} color="primary"/>;
	}

	return (
		<Container>
			<VerticalCentered><InfoIcon /><Box padding={1}> If you would like to create a Simple Group ensure the CSV file contains only the following 3 columns: <Box display="inline" fontWeight="bold">Name, Surname, Contact number</Box>, including a header row.</Box></VerticalCentered>
			<VerticalCentered><InfoIcon /><Box padding={1}> If you would like to create a <ContactsIcon style={{ fontSize: 12 }} /> Contact Group ensure the CSV file contains the following columns: <Box display="inline" fontWeight="bold">Name, Surname, Contact number</Box> (including a header row), and at least one custom field (<Box display="inline" fontWeight="bold">There is a max of 30 custom fields</Box>). Futher fields can be added with any names e.g. <Box display="inline" fontWeight="bold">Division, Facility, etc..</Box> (this name must also be included in the header row)</Box></VerticalCentered>
			<VerticalCentered><WarningIcon /><Box padding={1}>A maximum of <Box display="inline" fontWeight="bold">10,000</Box> contacts are allowed per group</Box></VerticalCentered>
			<TextField value={name} onChange={handleNameChange} label="Group Name" variant="outlined" />

			<CSVReaderContainer>
				{renderIcon()}
				<CSVReader
					label="Upload CSV file: "
					parserOptions={{
						header: true,
						transformHeader: (header) => { return header.trim().replace(/\s/g, "_").toLowerCase() },
						transform: (value) => { return value.trim() },
						skipEmptyLines: true,
					}}
					inputId="react-csv-reader-input"
					disabled={csvUploadPending}
					onFileLoaded={handleCsvFileLoaded}
					key={inputFieldKey}
				/>
			</CSVReaderContainer>

			<ButtonContainer>
				<Button variant="contained" color="primary" onClick={handleSubmit} disabled={!formValid || csvUploadPending || create_group_pending}>
					Submit
				</Button>
			</ButtonContainer>

			<Dialog open={Boolean(errorReport)} onClose={handleCancelErrors} maxWidth="md" fullWidth>
				<DialogTitle>Some CSV rows could not be imported</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{errorReport && (
							<>
								Found <b>{errorReport.invalid.length}</b> invalid row(s) out of{' '}
								<b>{errorReport.valid.length + errorReport.invalid.length}</b>. You can
								proceed with the <b>{errorReport.valid.length}</b> valid contacts and
								the invalid rows will be skipped, download the error list to fix them
								offline, or cancel to upload a corrected file.
							</>
						)}
					</DialogContentText>
					<ErrorList>
						<ErrorRow>
							<HeaderCell>Line</HeaderCell>
							<HeaderCell>Name</HeaderCell>
							<HeaderCell>Surname</HeaderCell>
							<HeaderCell>Contact number</HeaderCell>
							<HeaderCell>Reason</HeaderCell>
						</ErrorRow>
						{errorReport && errorReport.invalid.slice(0, ERROR_PREVIEW_LIMIT).map((e) => (
							<ErrorRow key={e.line}>
								<Cell>{e.line}</Cell>
								<Cell>{e.name}</Cell>
								<Cell>{e.surname}</Cell>
								<Cell>{e.contact_number}</Cell>
								<Cell>{e.reason}</Cell>
							</ErrorRow>
						))}
					</ErrorList>
					{errorReport && errorReport.invalid.length > ERROR_PREVIEW_LIMIT && (
						<DialogContentText>
							… and {errorReport.invalid.length - ERROR_PREVIEW_LIMIT} more. Use
							<b> Download errors </b> to see the full list.
						</DialogContentText>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelErrors}>Cancel</Button>
					<Button onClick={handleDownloadErrors}>Download errors</Button>
					<Button onClick={handleProceedWithValid} variant="contained" color="primary">
						Proceed with {errorReport ? errorReport.valid.length : 0} valid
					</Button>
				</DialogActions>
			</Dialog>

		</Container>
	)
}

const ERROR_PREVIEW_LIMIT = 50;

const CSVReaderContainer = styled.div`
	display: grid;
	grid-template-columns: 21px 1fr;
	grid-template-rows: 21px;
	align-items: center;
	gap: 10px;
`;

const Container = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	grid-gap: 16px;
	padding: 48px;
`;

const ButtonContainer = styled.div`
	display: flex;
	justify-content: center;
`;

const VerticalCentered = styled.div`
	display: flex;
	align-items: center;
`;

const ErrorList = styled.div`
	margin-top: 16px;
	max-height: 320px;
	overflow-y: auto;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
`;

const ErrorRow = styled.div`
	display: grid;
	grid-template-columns: 70px 1fr 1fr 160px 2fr;
	gap: 8px;
	padding: 6px 12px;
	border-bottom: 1px solid #f0f0f0;
	font-size: 13px;
	&:last-child { border-bottom: none; }
`;

const HeaderCell = styled.div`
	font-weight: 600;
	background: #fafafa;
`;

const Cell = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export default CreateGroup;
